import { useCallback, useEffect, useMemo, useState } from 'react';
import { listCoreTags, tagColorClass, type CoreTag } from '@/lib/coreSocle';

interface TagSelectorProps {
  /** Organisation (socle Core) propriétaire des tags. */
  organizationId: number | null;
  /** Ids de tags sélectionnés (contrôlé par le parent). */
  value: number[];
  /** Callback de mise à jour de la sélection. */
  onChange: (ids: number[]) => void;
  /** Portée pour filtrer les tags pertinents (produit / service). */
  scope?: 'product' | 'service';
  /** Libellé optionnel au-dessus des puces. */
  label?: string;
  /** Masque l'aide pointant vers le Socle central. */
  hideHint?: boolean;
}

/**
 * Sélecteur de tags réutilisable branché sur le socle central (core_tags).
 * Utilisé par les fiches Produit & Service pour associer des étiquettes
 * transverses sans recréer de listes locales.
 */
export default function TagSelector({
  organizationId,
  value,
  onChange,
  scope,
  label,
  hideHint,
}: TagSelectorProps) {
  const [tags, setTags] = useState<CoreTag[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!organizationId) {
      setTags([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      setTags(await listCoreTags(organizationId));
    } catch {
      setTags([]);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    load();
  }, [load]);

  const available = useMemo(() => {
    if (!scope) return tags;
    return tags.filter((t) => !t.scope || t.scope === 'both' || t.scope === scope);
  }, [tags, scope]);

  const toggle = (id: number) => {
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  };

  return (
    <div>
      {label && (
        <span className="block text-sm font-semibold text-foreground-800 mb-1.5">{label}</span>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-xs text-foreground-400 py-2">
          <i className="ri-loader-4-line animate-spin"></i>Chargement des tags…
        </div>
      ) : available.length === 0 ? (
        <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-background-100 text-xs text-foreground-500">
          <i className="ri-hashtag text-foreground-400"></i>
          <span>
            Aucun tag disponible. Créez-en dans <span className="font-semibold text-foreground-700">Socle central → Tags</span>.
          </span>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {available.map((t) => {
            const active = value.includes(t.id);
            const inactive = t.status === 'inactive';
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => toggle(t.id)}
                disabled={inactive}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium whitespace-nowrap transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed ${
                  active
                    ? `${tagColorClass(t.color)} ring-2 ring-primary-300`
                    : 'bg-background-50 border-background-200/70 text-foreground-600 hover:bg-background-100'
                }`}
                title={inactive ? 'Tag désactivé' : undefined}
              >
                <i className={active ? 'ri-check-line' : 'ri-price-tag-2-line'}></i>
                {t.name}
                {inactive && <i className="ri-eye-off-line opacity-60"></i>}
              </button>
            );
          })}
        </div>
      )}

      {!hideHint && available.length > 0 && (
        <p className="text-[11px] text-foreground-400 mt-1.5">
          Les tags proviennent du socle central et sont partagés par tous vos modules.
        </p>
      )}
    </div>
  );
}